import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { isStatusKeyedResponse } from "entities/charts/eva/requests/wellStatus";
import {
  getSplitYearUnit,
  isSplitYearResponseFresh
} from "entities/charts/eva/translators/splitYear";
import { RootState } from "store/rootReducer";
import { RigFracActivityChartRequestType, isAllStatusesRequest } from "types";
import formatPropertyName from "utils/column/formatPropertyName";
import {
  applyFieldNameMode,
  resolveSimplifyCustomFieldNames
} from "utils/column/getColumnLabelWithContext";

import { useChartDependencies, useChartEntities } from "hooks/charts";
import useColumnsDictionary from "hooks/useColumnsDictionary";

import { EntityKind } from "models/entityKind";

import { getRigFracGroupByLabel } from "components/charts/rig-frac/groupByOptions";
import { applyRemoveUUID } from "components/charts/utils";
import { addNormalizationBasisToTitle } from "components/charts/utils/addNormalizationBasisToTitle";
import { useScreenshotContext } from "components/screenshot/hooks";
import { applyAbbreviations } from "components/screenshot/utils";
import { useUserSettings } from "components/user/hooks";

const useChartLegendTitle = (): string => {
  // state from store
  const { id, entityKind } = useChartsContext();
  const {
    screenshot,
    chartFocus,
    multiProductFocus,
    normalizeBy,
    chartType,
    splitYear,
    chartTypeParam,
    apiResponse,
    histogramToggle,
    request
  } = useChartEntities(id);

  // Rig/Frac charts (EVA-76): the legend groups by the request's group-by
  // dimension, not the wells global group-by — so the heading must be that
  // dimension's label ("Operator", "Basin", …), not the wells field title.
  const isRigFracChart =
    chartType === EvaChart.RigFracActivity ||
    chartType === EvaChart.RigFracRecentActivity;
  const rigFracGroupByLabel = isRigFracChart
    ? getRigFracGroupByLabel(
        (request as unknown as Maybe<Partial<RigFracActivityChartRequestType>>)?.groupBy
      )
    : null;

  // Well Status Current in ALL STATUSES mode: the legend lists statuses, not the
  // focus bins (see useChartLegendItems), so the heading must name that dimension
  // instead of the group-by field — otherwise it reads "Resource Play" above a
  // list of Producing / Suspended / Shut In. Same treatment as Split Year's
  // "Quarter" and multi-product's "Products".
  const isAllStatusesMode = isAllStatusesRequest(request);

  const isChartFocusActive = chartFocus?.active;
  const isScreenshotActive = screenshot?.active;
  const chartFocusTitle = chartFocus?.properties?.groupBy.title;

  const { groupBy, txnId, useNormalizeBy } = useChartDependencies(entityKind);

  const { userAbbreviations } = useUserSettings();
  const screenshotState = useScreenshotContext();
  const wellGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const columnsDictionary = useColumnsDictionary(entityKind);

  //useNormalizeBy is set by the global normalize field
  const isGlobalNormalizeByActive = useNormalizeBy;

  // state
  const [value, setValue] = useState<string>("");

  const abbreviations = userAbbreviations?.abbreviations ?? [];

  useEffect(() => {
    if (isRigFracChart) {
      setValue(rigFracGroupByLabel ?? "");
      return;
    }

    // Ahead of the `legendTitle` guard below on purpose: this heading describes
    // the chart's own series, so it must not depend on the map legend having a
    // title at all.
    //
    // Gated on the RESPONSE as well as the mode, and on the same predicate the
    // legend items use. The flag flips the instant the user picks All Statuses,
    // while `apiResponse` still holds one series per focus BIN until the fetch
    // returns — so switching on the flag alone put "Status" above "Montney /
    // Duvernay" for the whole fetch. That is the bug the items-side guard exists
    // to prevent, and guarding only the items just moved it to the heading. Same
    // rule the Split Year branch below states: the heading and the swatches must
    // never disagree.
    if (isAllStatusesMode && isStatusKeyedResponse(apiResponse?.series)) {
      setValue("Status");
      return;
    }

    const legendTitle =
      entityKind == EntityKind.Well ? txnId.legend.title : txnId.facilityLegend.title;

    // Hoisted above the WellContribution early return below, which also needs
    // it — see the EVA-127 comment further down for what this value means.
    const simplifyCustomFieldNames = resolveSimplifyCustomFieldNames(
      isScreenshotActive,
      screenshotState?.settings
    );

    if (chartType === EvaChart.WellContribution) {
      if (!isChartFocusActive) {
        // Route through applyFieldNameMode like every other legend title:
        // updateGroupBy no longer bakes the subgroup prefix or unit into the
        // stored title, so skipping this left Well Contribution rendering a
        // bare field name with neither.
        setValue(
          applyFieldNameMode(wellGroupBy.title, wellGroupBy, simplifyCustomFieldNames)
        );
        return;
      }
    }
    // Backend-driven Split Year (EVA-40): re-groups the legend by quarter/month instead
    // of the active Group By field — the heading must say so instead of showing the
    // pre-reshape field title. Only takes over when chart-svc actually applied the
    // reshape (`splitYearApplied: true`), mirroring the legend items' own fallback so
    // the heading and the swatches never disagree. Also requires the freshness check
    // (see isSplitYearResponseFresh) so toggling off doesn't keep reading the stale,
    // still-reshaped `apiResponse` until the debounced fetch resolves.
    if (
      (chartType === EvaChart.StackedBar || chartType === EvaChart.StackedBarMidstream) &&
      splitYear?.active &&
      isSplitYearResponseFresh(request, splitYear.active, histogramToggle?.active) &&
      apiResponse?.splitYearApplied
    ) {
      const splitYearUnit = getSplitYearUnit(chartTypeParam?.properties?.x);
      if (splitYearUnit) {
        setValue(splitYearUnit === "Year-Qtr" ? "Quarter" : "Month");
        return;
      }
    }
    if (
      (chartType === EvaChart.TotalRateDate ||
        chartType === EvaChart.TotalRateDateMidstream) &&
      multiProductFocus?.active &&
      multiProductFocus?.properties?.products?.length > 0
    ) {
      setValue("Products");
      return;
    }

    if (!legendTitle) {
      return;
    }

    const title = isChartFocusActive ? chartFocusTitle : groupBy.title;

    const isForecastEnabled = title.includes("Forecast");

    let nextTitle = isForecastEnabled ? applyRemoveUUID(title) : title;

    // EVA-127 "Simplify Custom Field Names": single source of truth lives in
    // applyFieldNameMode. `undefined` (not in a chart screenshot) is
    // interpreted identically to OFF — subgroup-qualified for custom fields
    // (never the category — EVA-6338), decorated as usual for every other
    // type; ON strips down to `{field} ({unit})`.
    //
    // FIX 4: chart focus's group-by is sanitized (sanitizeObject via
    // DEFAULT_CHART_FOCUS_ENTITY's allow-list) before it's persisted on a
    // restored dashboard — it drops subgroup/defaultUnit/metadata, so
    // applyFieldNameMode can't correctly qualify or strip it. Re-resolve the
    // FULL column from columnsDictionary by property (same lookup
    // qualifyAxisName uses on the axis render path), so the legend matches
    // the axis for the same field. Falls back to the sanitized focus object
    // when the lookup misses (unresolved property, still-loading dictionary).
    // useColumnsDictionary also returns `{}` on the very first render (it
    // populates the dict in an effect, not during render), so that fallback
    // is also what renders on first paint — benign, since the effect re-runs
    // once columns load and this hook's own effect re-fires on the
    // `columnsDictionary` dependency below.
    const activeGroupBy = isChartFocusActive
      ? (chartFocus?.properties?.groupBy?.property &&
          columnsDictionary?.[
            formatPropertyName(chartFocus.properties.groupBy.property)
          ]) ||
        chartFocus?.properties?.groupBy
      : groupBy;
    // Must run BEFORE addNormalizationBasisToTitle below: applyFieldNameMode is
    // what appends the unit for a custom field (Phase 1 dropped it from the
    // stored title). addNormalizationBasisToTitle finds a known unit token
    // INSIDE the title and rewrites it in place — running it first found no
    // unit yet, so `String.replace(undefined, …)` no-op'd and the normalization
    // basis silently never appeared for a normalized custom field.
    nextTitle = applyFieldNameMode(nextTitle, activeGroupBy, simplifyCustomFieldNames);

    nextTitle =
      isGlobalNormalizeByActive && !isChartFocusActive
        ? addNormalizationBasisToTitle(nextTitle, normalizeBy?.properties)
        : nextTitle;

    const formattedTitle =
      isScreenshotActive && screenshotState?.settings?.applyAbbreviations
        ? applyAbbreviations(nextTitle, abbreviations)
        : nextTitle;

    setValue(formattedTitle);
  }, [
    groupBy,
    txnId.legend.title,
    chartType,
    multiProductFocus,
    isChartFocusActive,
    chartFocusTitle,
    isGlobalNormalizeByActive,
    isScreenshotActive,
    screenshotState?.settings?.simplifyCustomFieldNames,
    normalizeBy,
    abbreviations,
    splitYear,
    chartTypeParam,
    apiResponse,
    isRigFracChart,
    rigFracGroupByLabel,
    isAllStatusesMode,
    request,
    histogramToggle,
    chartFocus?.properties?.groupBy,
    columnsDictionary
  ]);

  return value;
};

export default useChartLegendTitle;
